// Network impairment simulator + bandwidth meter for the multiplayer client.
//
// Runs *on the client* so it can exercise prediction/reconciliation without
// server-side support: outgoing command batches and incoming snapshots are
// delayed (one-way latency + jitter) and optionally dropped before they reach
// the socket / the snapshot handler. The WebSocket transport is reliable, so
// loss here is a deliberate stress test, not a real-world condition.
//
// Also a rolling 1-second byte + message rate meter (upstream/downstream
// bandwidth), used by the `?netstats=1` readout and `window.__webrace.mp()`.

/** Deterministic PRNG so impairment is reproducible in tests. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface NetSimConfig {
  /** One-way delay in ms applied by this link. */
  delayMs: number;
  /** Random +/- jitter in ms. */
  jitterMs: number;
  /** Drop probability, 0..100. */
  lossPct: number;
}

/** A one-way delayed/dropped queue of messages. */
export class NetSim<T> {
  private queue: { at: number; msg: T }[] = [];
  private rng = mulberry32(0x9e3779b9);
  /** Last scheduled arrival — keeps delivery order (TCP doesn't reorder). */
  private lastAt = -Infinity;

  constructor(public config: NetSimConfig) {}

  get pending(): number {
    return this.queue.length;
  }

  /** Enqueue a message, scheduled to arrive at `now + delay + jitter`. */
  push(msg: T, now: number): void {
    if (this.config.lossPct > 0 && this.rng() * 100 < this.config.lossPct) return;
    const j =
      this.config.jitterMs > 0 ? (this.rng() * 2 - 1) * this.config.jitterMs : 0;
    // Clamp to the previous arrival so jitter adds delay but never reorders
    // (the WebSocket transport is ordered).
    const at = Math.max(this.lastAt, now + Math.max(0, this.config.delayMs + j));
    this.lastAt = at;
    this.queue.push({ at, msg });
  }

  /** Move every message due by `now` into `out`. */
  drain(now: number, out: T[]): void {
    while (this.queue.length > 0 && this.queue[0].at <= now) {
      out.push(this.queue.shift()!.msg);
    }
  }
}

/** Rolling 1-second byte + message rate meter. */
export class RateMeter {
  private samples: { t: number; bytes: number }[] = [];

  add(bytes: number, now: number): void {
    this.samples.push({ t: now, bytes });
    this.prune(now);
  }

  private prune(now: number): void {
    while (this.samples.length > 0 && now - this.samples[0].t > 1000) {
      this.samples.shift();
    }
  }

  /** Bytes in the trailing second. */
  bytesPerSec(now: number): number {
    this.prune(now);
    let n = 0;
    for (const s of this.samples) n += s.bytes;
    return n;
  }

  /** Messages in the trailing second. */
  msgsPerSec(now: number): number {
    this.prune(now);
    return this.samples.length;
  }
}
